import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Inbox, Star, Lightbulb, Loader2, RefreshCw, Filter,
  CheckCircle, Clock, XCircle, Rocket, AlertTriangle, Trash2, MessageSquare,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_META = {
  submitted:   { label: "Ingediend",        Icon: Clock,        color: "bg-blue-100 text-blue-700 border-blue-200" },
  in_review:   { label: "In beoordeling",  Icon: Clock,        color: "bg-yellow-100 text-yellow-700 border-yellow-200" },
  approved:    { label: "Goedgekeurd",     Icon: CheckCircle,  color: "bg-green-100 text-green-700 border-green-200" },
  rejected:    { label: "Afgewezen",        Icon: XCircle,      color: "bg-red-100 text-red-700 border-red-200" },
  implemented: { label: "Geïmplementeerd",  Icon: Rocket,       color: "bg-purple-100 text-purple-700 border-purple-200" },
};

const SCORE_COLOR = (s) => s >= 4 ? "text-green-600" : s === 3 ? "text-amber-600" : "text-red-600";
const SENTIMENT_COLOR = {
  positive: "text-green-600",
  neutral:  "text-gray-500",
  negative: "text-red-600",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso.slice(0, 16); }
};

const ProposalRow = ({ p, onUpdate, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(p.review_notes || "");
  const [saving, setSaving] = useState(false);
  const meta = STATUS_META[p.status] || STATUS_META.submitted;

  const setStatus = async (newStatus) => {
    setSaving(true);
    try {
      await onUpdate(p.proposal_id, newStatus, notes);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-200 bg-white" data-testid={`inbox-proposal-${p.proposal_id}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left px-3 py-2 flex items-start justify-between gap-2 hover:bg-gray-50"
      >
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold truncate">{p.title || "(geen titel)"}</p>
          <p className="text-[10px] font-mono text-gray-500 mt-0.5">
            {p.proposal_id} · {fmtDate(p.submitted_at)} · {p.category || "feature"} · {p.estimated_complexity || "—"}
          </p>
        </div>
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 border ${meta.color} whitespace-nowrap`}>
          <meta.Icon className="w-3 h-3" /> {meta.label}
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 pt-2 border-t border-gray-100 space-y-2 text-xs">
          {p.summary && <p className="text-gray-700">{p.summary}</p>}
          {p.user_goal && <p><span className="font-bold">Doel:</span> {p.user_goal}</p>}
          {p.benefits?.length > 0 && (
            <div><span className="font-bold">Voordelen:</span>
              <ul className="list-disc ml-4 text-[11px]">{p.benefits.map((b, i) => <li key={`b-${i}-${b.slice(0,12)}`}>{b}</li>)}</ul>
            </div>
          )}
          {p.risks?.length > 0 && (
            <div><span className="font-bold">Risico's:</span>
              <ul className="list-disc ml-4 text-[11px]">{p.risks.map((r, i) => <li key={`r-${i}-${r.slice(0,12)}`}>{r}</li>)}</ul>
            </div>
          )}
          {p.source_idea && (
            <details className="text-[11px] text-gray-600">
              <summary className="cursor-pointer">Origineel idee van gebruiker</summary>
              <p className="mt-1 whitespace-pre-wrap pl-2 border-l-2 border-gray-200">{p.source_idea}</p>
            </details>
          )}

          <div>
            <p className="text-[10px] font-mono text-gray-500 mt-2 mb-1">Beoordeling / notities</p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full text-[11px] border border-gray-200 px-2 py-1 focus:outline-none focus:border-[#002FA7]"
              rows={2}
              placeholder="Optionele toelichting voor de gebruiker..."
              data-testid={`inbox-notes-${p.proposal_id}`}
            />
          </div>

          <div className="flex flex-wrap gap-1 pt-1">
            {Object.entries(STATUS_META).map(([k, m]) => (
              <button
                key={k}
                onClick={() => setStatus(k)}
                disabled={saving || p.status === k}
                className={`text-[10px] font-bold px-2 py-1 border ${m.color} disabled:opacity-100 disabled:cursor-default ${p.status !== k ? "hover:scale-105" : "ring-1 ring-gray-900"}`}
                data-testid={`inbox-set-status-${p.proposal_id}-${k}`}
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin inline" /> : m.label}
              </button>
            ))}
            <button
              onClick={() => onDelete(p.proposal_id)}
              className="ml-auto text-[10px] text-red-600 hover:text-red-800"
              title="Verwijderen"
              data-testid={`inbox-delete-${p.proposal_id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const FeedbackRow = ({ f }) => (
  <div className="border border-gray-200 bg-white px-3 py-2" data-testid={`inbox-feedback-${f.timestamp}`}>
    <div className="flex items-start justify-between gap-2 mb-1">
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={`s-${i}-${f.score}`} className={`w-3.5 h-3.5 ${i < f.score ? "fill-current " + SCORE_COLOR(f.score) : "text-gray-200"}`} />
        ))}
        {f.sentiment && (
          <span className={`text-[10px] font-mono ml-2 ${SENTIMENT_COLOR[f.sentiment] || "text-gray-500"}`}>
            {f.sentiment}
          </span>
        )}
      </div>
      <p className="text-[10px] font-mono text-gray-400">{fmtDate(f.timestamp)}</p>
    </div>
    {f.comment && <p className="text-xs text-gray-700 whitespace-pre-wrap">{f.comment}</p>}
    {f.discussion_id && (
      <a
        href={`/discussion/${f.discussion_id}`}
        target="_blank" rel="noopener noreferrer"
        className="text-[10px] font-mono text-[#002FA7] hover:underline mt-1 inline-block"
      >
        → bekijk debat {f.discussion_id.slice(0, 8)}…
      </a>
    )}
  </div>
);

export const FeedbackInbox = () => {
  const [tab, setTab] = useState("proposals");
  const [proposals, setProposals] = useState([]);
  const [proposalCounts, setProposalCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [feedback, setFeedback] = useState([]);
  const [fbCounts, setFbCounts] = useState({ by_score: {}, by_sentiment: {}, total: 0 });
  const [minScore, setMinScore] = useState(1);
  const [maxScore, setMaxScore] = useState(5);
  const [loading, setLoading] = useState(false);

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter ? `${API}/inbox/proposals?status=${statusFilter}` : `${API}/inbox/proposals`;
      const r = await axios.get(url);
      setProposals(r.data.proposals || []);
      setProposalCounts(r.data.counts || {});
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("[Inbox] proposals:", err?.message);
    } finally { setLoading(false); }
  }, [statusFilter]);

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/inbox/feedback?min_score=${minScore}&max_score=${maxScore}`);
      setFeedback(r.data.feedback || []);
      setFbCounts({ by_score: r.data.by_score || {}, by_sentiment: r.data.by_sentiment || {}, total: r.data.total || 0 });
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("[Inbox] feedback:", err?.message);
    } finally { setLoading(false); }
  }, [minScore, maxScore]);

  useEffect(() => {
    if (tab === "proposals") fetchProposals();
    else fetchFeedback();
  }, [tab, fetchProposals, fetchFeedback]);

  const updateProposal = async (id, status, review_notes) => {
    try {
      await axios.patch(`${API}/inbox/proposals/${id}`, { status, review_notes });
      toast.success(`Status: ${STATUS_META[status]?.label || status}`);
      fetchProposals();
    } catch {
      toast.error("Status wijzigen mislukt");
    }
  };

  const deleteProposal = async (id) => {
    if (!window.confirm("Voorstel definitief verwijderen?")) return;
    try {
      await axios.delete(`${API}/inbox/proposals/${id}`);
      toast.success("Verwijderd");
      fetchProposals();
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  return (
    <div className="bg-white border-2 border-gray-200 shadow-sm" data-testid="feedback-inbox-panel">
      <div className="bg-[#002FA7] text-white px-4 py-3 flex items-center justify-between">
        <span className="flex items-center gap-2 font-bold text-sm" style={{ fontFamily: "Chivo, sans-serif" }}>
          <Inbox className="w-4 h-4" />
          Inbox
        </span>
        <button
          onClick={() => tab === "proposals" ? fetchProposals() : fetchFeedback()}
          disabled={loading}
          className="opacity-80 hover:opacity-100"
          title="Vernieuwen"
          data-testid="inbox-refresh"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setTab("proposals")}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-wider ${
            tab === "proposals" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"
          }`}
          data-testid="inbox-tab-proposals"
        >
          <Lightbulb className="w-3 h-3" />
          Voorstellen
          {proposalCounts.all > 0 && (
            <span className="ml-1 text-[9px] px-1.5 py-0.5 bg-amber-500 text-white rounded-full">
              {proposalCounts.all}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab("feedback")}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-wider ${
            tab === "feedback" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"
          }`}
          data-testid="inbox-tab-feedback"
        >
          <Star className="w-3 h-3" />
          Reviews
          {fbCounts.total > 0 && (
            <span className="ml-1 text-[9px] px-1.5 py-0.5 bg-amber-500 text-white rounded-full">
              {fbCounts.total}
            </span>
          )}
        </button>
      </div>

      {/* Proposals tab */}
      {tab === "proposals" && (
        <div className="p-3 space-y-3" data-testid="inbox-proposals-tab">
          <div className="flex flex-wrap gap-1 items-center">
            <span className="text-[10px] font-mono uppercase text-gray-500 mr-1"><Filter className="w-3 h-3 inline" /> filter:</span>
            <button onClick={() => setStatusFilter("")}
              className={`text-[10px] px-2 py-0.5 border ${!statusFilter ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-300"}`}
              data-testid="filter-status-all"
            >
              alle ({proposalCounts.all || 0})
            </button>
            {Object.entries(STATUS_META).map(([k, m]) => (
              <button key={k} onClick={() => setStatusFilter(k)}
                className={`text-[10px] px-2 py-0.5 border ${statusFilter === k ? "bg-gray-900 text-white border-gray-900" : `bg-white ${m.color.replace('bg-', 'border-').split(' ')[2] || 'border-gray-300'} text-gray-700`}`}
                data-testid={`filter-status-${k}`}
              >
                {m.label} ({proposalCounts[k] || 0})
              </button>
            ))}
          </div>

          {proposals.length === 0 ? (
            <p className="text-[11px] text-gray-500 italic text-center py-4">
              {statusFilter ? "Geen voorstellen met deze status." : "Nog geen voorstellen ontvangen."}
            </p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {proposals.map((p) => (
                <ProposalRow key={p.proposal_id} p={p} onUpdate={updateProposal} onDelete={deleteProposal} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Feedback tab */}
      {tab === "feedback" && (
        <div className="p-3 space-y-3" data-testid="inbox-feedback-tab">
          {/* Score distribution */}
          <div className="grid grid-cols-5 gap-1 text-center" data-testid="feedback-distribution">
            {[1,2,3,4,5].map((s) => {
              const count = fbCounts.by_score[s] || 0;
              const pct = fbCounts.total > 0 ? Math.round((count / fbCounts.total) * 100) : 0;
              return (
                <button key={s} onClick={() => { setMinScore(s); setMaxScore(s); }}
                  className="border border-gray-200 p-1.5 hover:border-gray-700"
                  data-testid={`feedback-score-${s}`}
                >
                  <div className={`flex justify-center gap-0.5 mb-0.5 ${SCORE_COLOR(s)}`}>
                    {Array.from({ length: s }).map((_, i) => <Star key={`fb-${s}-${i}`} className="w-2 h-2 fill-current" />)}
                  </div>
                  <p className="text-[10px] font-bold">{count}</p>
                  <p className="text-[9px] text-gray-400">{pct}%</p>
                </button>
              );
            })}
          </div>

          <div className="flex gap-1 items-center text-[10px]">
            <span className="font-mono uppercase text-gray-500">Filter score:</span>
            <button onClick={() => { setMinScore(1); setMaxScore(5); }} className="px-2 py-0.5 border border-gray-300 hover:bg-gray-50">alle</button>
            <button onClick={() => { setMinScore(1); setMaxScore(2); }} className="px-2 py-0.5 border border-red-300 text-red-700 hover:bg-red-50">laag (1-2)</button>
            <button onClick={() => { setMinScore(4); setMaxScore(5); }} className="px-2 py-0.5 border border-green-300 text-green-700 hover:bg-green-50">hoog (4-5)</button>
          </div>

          {feedback.length === 0 ? (
            <p className="text-[11px] text-gray-500 italic text-center py-4">Geen reviews binnen dit filter.</p>
          ) : (
            <div className="space-y-2 max-h-[380px] overflow-y-auto">
              {feedback.map((f) => <FeedbackRow key={`${f.timestamp}-${f.score}`} f={f} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
